import React, { useState, useRef, useCallback, useEffect, useLayoutEffect, useMemo } from 'react';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { Fancybox } from '@fancyapps/ui';
import '@fancyapps/ui/dist/fancybox/fancybox.css';
import { ChevronDown, ChevronLeft, ChevronRight, ImagePlus, Maximize2, Plus, Trash2, MoreVertical, Loader2 } from 'lucide-react';
import { BreadcrumbComponent } from '../../Components/BreadCrumb';
import { useBuilding } from '../../Helpers/BuildingContext';
import { useBuildingKey } from '../../Helpers/utils';
import { useBuildingImages, SelectedImage, ImageSection, SubSection, LevelGroup, RoomGroup, CategoryGroup, UtilityGroupingMode, isRoomGroupedImageSection } from '../../hooks/useBuildingImages';
import { placementLabel } from '../../hooks/buildingImageGrouping';
import { useFirebaseImage, ImageSize, getImagePathWithSize, resolveFirebaseImageUrl } from '../../Helpers/ImageLoader';
import { formatDateTimeForDisplay } from '../../Helpers/DateUtils';
import { BaseComponent, RoomModel, Building } from '../../types/protocols';
import { BaseType } from '../../types/Enums';
import ImageUploadModal from './ImageUploadModal';
import LocationDisplay, { LocationData, useComponentLocations } from '../../Components/LocationDisplay';
import { LocationOverlay, LocationViewer } from '../../Components/LocationViewer';
import MultiMarkerImage from '../../Components/MultiMarkerImage';
import { useIsSmallScreen } from '../../hooks/useIsSmallScreen';
import { Link } from 'react-router-dom';
import { useOpenPath } from '../../hooks/useOpenPath';
import './UnifiedBuildingImages.css';

// Lucide icons as markup for the Fancybox toolbar, which takes HTML templates
const toolbarIcon = (paths: string) =>
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" stroke-linecap="round" stroke-linejoin="round">${paths}</svg>`;
const OPEN_ICON = toolbarIcon('<path d="M15 3h6v6"/><path d="M10 14 21 3"/><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>');
const TRASH_ICON = toolbarIcon('<path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/><line x1="10" x2="10" y1="11" y2="17"/><line x1="14" x2="14" y1="11" y2="17"/>');

// Full-screen caption: name, placement (room-bound components) and date
const viewerCaption = (image: SelectedImage): string => {
    const placement = image.type === 'component' && image.placement ? ` (${placementLabel(image.placement)})` : '';
    return `${image.displayName}${placement} - ${formatDateTimeForDisplay(image.photo.date)}`;
};

/** The page of the component or room a photo belongs to. */
const detailPathOf = (buildingKey: string, image: SelectedImage): string => image.type === 'component'
    ? `/building/${buildingKey}/component/${image.componentKey}`
    : `/building/${buildingKey}/room/${image.roomKey}`;

// Thumbnail component with date display
const ImageThumbnail = React.memo<{
    image: SelectedImage;
    isSelected: boolean;
    onClick: () => void;
}>(({ image, isSelected, onClick }) => {
    const thumbnailPath = getImagePathWithSize(image.photo.storagePath, ImageSize.THUMBNAIL_SMALL);
    const { src, isLoading, hasError } = useFirebaseImage(thumbnailPath);

    return (
        <div
            className={`unified-images-thumbnail ${isSelected ? 'selected' : ''}`}
            onClick={onClick}
        >
            {isLoading ? (
                <div className="unified-images-thumbnail-loading">Laddar...</div>
            ) : hasError ? (
                <div className="unified-images-thumbnail-error">Fel</div>
            ) : (
                <img src={src} alt={image.displayName} />
            )}
            <div className="unified-images-thumbnail-date">{image.photo.date.substring(0, 10)}</div>
        </div>
    );
});

// Preview pane component - click to open in Fancybox for zoom/pan
const ImagePreview: React.FC<{
    image: SelectedImage;
    isAdmin: boolean;
    deleting: boolean;
    onDelete: () => void;
    /** The component or room page, opened in a new tab from the name. */
    detailPath: string;
}> = ({ image, isAdmin, deleting, onDelete, detailPath }) => {
    const { src: fullSrc, isLoading, hasError } = useFirebaseImage(image.photo.storagePath);

    const handleClick = useCallback(() => {
        if (!fullSrc) return;
        Fancybox.show([{
            src: fullSrc,
            caption: viewerCaption(image)
        }]);
    }, [fullSrc, image]);

    if (hasError) {
        return (
            <div className="unified-images-preview-error">
                Kunde inte ladda bild
            </div>
        );
    }

    return (
        <div className="unified-images-preview-container">
            <div className="unified-images-preview-image-wrapper">
                {isLoading ? (
                    <Loader2 className="unified-images-preview-spinner" size={32} />
                ) : fullSrc && (
                    <img
                        src={fullSrc}
                        alt={image.displayName}
                        onClick={handleClick}
                        style={{ cursor: 'pointer' }}
                        draggable={false}
                    />
                )}
            </div>

            <div className="unified-images-preview-info">
                <Link className="unified-images-preview-name" to={detailPath} target="_blank">
                    {image.displayName}
                </Link>
                {image.type === 'component' && image.placement && (
                    <div className="unified-images-preview-placement">
                        {placementLabel(image.placement)}
                    </div>
                )}
                <div className="unified-images-preview-date">
                    {formatDateTimeForDisplay(image.photo.date)}
                </div>
                {isAdmin && (
                    <button
                        className="unified-images-delete-button"
                        onClick={onDelete}
                        disabled={deleting}
                    >
                        <Trash2 size={16} />
                        {deleting ? 'Tar bort...' : 'Ta bort'}
                    </button>
                )}
            </div>
        </div>
    );
};

// Top-level section header (BaseType) with optional grouping menu for Fastighetsteknik
const SectionHeader: React.FC<{
    section: ImageSection;
    onToggle: () => void;
    utilityGroupingMode?: UtilityGroupingMode;
    onGroupingModeChange?: (mode: UtilityGroupingMode) => void;
}> = ({ section, onToggle, utilityGroupingMode, onGroupingModeChange }) => {
    const [menuOpen, setMenuOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);

    const isUtility = isRoomGroupedImageSection(section.baseType);

    // Close menu when clicking outside
    useEffect(() => {
        if (!menuOpen) return;
        const handleClickOutside = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
                setMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [menuOpen]);

    const handleMenuClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        setMenuOpen(!menuOpen);
    };

    const handleModeSelect = (mode: UtilityGroupingMode) => {
        onGroupingModeChange?.(mode);
        setMenuOpen(false);
    };

    return (
        <div className="unified-images-section-header" onClick={onToggle}>
            <div className="unified-images-section-title">
                {section.collapsed ? <ChevronRight size={20} /> : <ChevronDown size={20} />}
                <span>{section.label}</span>
                <span className="unified-images-section-count">({section.totalCount} bilder)</span>
            </div>
            {isUtility && onGroupingModeChange && (
                <div className="unified-images-section-menu" ref={menuRef}>
                    <button
                        className="unified-images-menu-button"
                        onClick={handleMenuClick}
                        title="Grupperingsalternativ"
                    >
                        <MoreVertical size={18} />
                    </button>
                    {menuOpen && (
                        <div className="unified-images-dropdown-menu">
                            <div className="unified-images-dropdown-label">Gruppera efter</div>
                            <button
                                className={`unified-images-dropdown-item ${utilityGroupingMode === 'room' ? 'active' : ''}`}
                                onClick={(e) => { e.stopPropagation(); handleModeSelect('room'); }}
                            >
                                Rum och plan
                            </button>
                            <button
                                className={`unified-images-dropdown-item ${utilityGroupingMode === 'category' ? 'active' : ''}`}
                                onClick={(e) => { e.stopPropagation(); handleModeSelect('category'); }}
                            >
                                Kategori
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// Subsection header (Component/Room) with drag and drop support. `showPlacement`
// is off under a room header, which already says where the component is.
const SubSectionHeader: React.FC<{
    subSection: SubSection;
    buildingKey: string;
    isDragOver: boolean;
    showAddButton: boolean;
    showPlacement: boolean;
    onAddClick: () => void;
    onDragOver: (e: React.DragEvent) => void;
    onDragLeave: (e: React.DragEvent) => void;
    onDrop: (e: React.DragEvent) => void;
}> = ({ subSection, buildingKey, isDragOver, showAddButton, showPlacement, onAddClick, onDragOver, onDragLeave, onDrop }) => {
    const namePath = subSection.componentKey
        ? `/building/${buildingKey}/component/${subSection.componentKey}`
        : subSection.roomKey ? `/building/${buildingKey}/room/${subSection.roomKey}` : null;

    return (
        <div
            className={`unified-images-subsection-header ${isDragOver ? 'drag-over' : ''}`}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
        >
            <div className="unified-images-subsection-heading">
                <div className="unified-images-subsection-title">
                    {namePath ? (
                        <Link
                            className="unified-images-subsection-link"
                            to={namePath}
                            target="_blank"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {subSection.label}
                        </Link>
                    ) : (
                        <span className="unified-images-subsection-link">{subSection.label}</span>
                    )}
                    <span className="unified-images-subsection-count">({subSection.totalCount})</span>
                </div>
                {showPlacement && subSection.placement && (
                    <div className="unified-images-subsection-placement">
                        {placementLabel(subSection.placement)}
                    </div>
                )}
            </div>
            {showAddButton && (
                <button
                    className="unified-images-add-button-small"
                    onClick={(e) => {
                        e.stopPropagation();
                        onAddClick();
                    }}
                    title="Lägg till bilder"
                >
                    <Plus size={12} />
                    Ladda upp
                </button>
            )}
        </div>
    );
};

// Level header for Fastighetsteknik hierarchical view
const LevelHeader: React.FC<{
    levelGroup: LevelGroup;
}> = ({ levelGroup }) => {
    return (
        <div className="unified-images-level-header">
            <span className="unified-images-level-name">{levelGroup.levelName}</span>
            <span className="unified-images-level-count">({levelGroup.totalCount} bilder)</span>
        </div>
    );
};

// Room header within a level for Fastighetsteknik
const RoomHeader: React.FC<{
    roomGroup: RoomGroup;
    buildingKey: string;
}> = ({ roomGroup, buildingKey }) => {
    return (
        <div className="unified-images-room-header">
            {roomGroup.roomKey !== '__unassigned__' ? (
                <Link
                    className="unified-images-room-name unified-images-room-link"
                    to={`/building/${buildingKey}/room/${roomGroup.roomKey}`}
                    target="_blank"
                    onClick={(e) => e.stopPropagation()}
                >
                    {roomGroup.roomName}
                </Link>
            ) : (
                <span className="unified-images-room-name">{roomGroup.roomName}</span>
            )}
            {roomGroup.unitName && (
                <span className="unified-images-room-unit">· {roomGroup.unitName}</span>
            )}
            <span className="unified-images-room-count">({roomGroup.totalCount})</span>
        </div>
    );
};

// Category header for Fastighetsteknik category view
const CategoryHeader: React.FC<{
    categoryGroup: CategoryGroup;
}> = ({ categoryGroup }) => {
    return (
        <div className="unified-images-category-header">
            <span className="unified-images-category-name">{categoryGroup.categoryName}</span>
            <span className="unified-images-category-count">({categoryGroup.totalCount} bilder)</span>
        </div>
    );
};

// A component's position with the component it belongs to, as the preview pane shows it
interface SelectedLocation {
    location: LocationData;
    component: BaseComponent;
}

// Component location thumbnails row using shared LocationDisplay
const ComponentLocationThumbnails: React.FC<{
    component: BaseComponent;
    building: Building;
    onLocationClick: (selected: SelectedLocation) => void;
}> = ({ component, building, onLocationClick }) => {
    const locations = useComponentLocations(component, building);

    if (locations.length === 0) {
        return null;
    }

    return (
        <div className="unified-images-location-thumbnails">
            <LocationDisplay
                locations={locations}
                onLocationClick={(location) => onLocationClick({ location, component })}
            />
        </div>
    );
};

// Main component
const UnifiedBuildingImages: React.FC = () => {
    const buildingKey = useBuildingKey();
    const { currentBuilding } = useBuilding();

    // Set document title
    useDocumentTitle(
        currentBuilding?.summary?.name
            ? `Samtliga bilder: ${currentBuilding.summary.name}`
            : 'Samtliga bilder'
    );

    const {
        sections,
        allImages,
        selectedImage,
        isAdmin,
        utilityGroupingMode,
        setUtilityGroupingMode,
        loading,
        error,
        deleting,
        setSelectedImage,
        toggleSection,
        collapseAll,
        deleteImage,
        clearError,
        refetchBuilding
    } = useBuildingImages({ buildingKey: buildingKey || '' });


    // Location selected from a map/blueprint/photo position thumbnail. When set, the
    // right pane shows the zoomable location viewer instead of the selected image,
    // and "Helskärm" opens the same position in an overlay.
    const [selectedLocation, setSelectedLocation] = useState<SelectedLocation | null>(null);
    const [locationFullscreen, setLocationFullscreen] = useState(false);

    const handleSelectLocation = useCallback((selected: SelectedLocation) => {
        setSelectedLocation(selected);
        setLocationFullscreen(false);
    }, []);

    // Selecting an image (via thumbnail or keyboard) clears any location preview so the
    // pane returns to showing the image.
    const handleSelectImage = useCallback((image: SelectedImage) => {
        setSelectedLocation(null);
        setSelectedImage(image);
    }, [setSelectedImage]);

    // Fönster drill-down selection state (page-local): which reference photo is
    // enlarged, and which window's photos are shown beneath it.
    const [activeReferencePhotoKey, setActiveReferencePhotoKey] = useState<string | null>(null);
    const [activeWindowKey, setActiveWindowKey] = useState<string | null>(null);

    // The Fönster section, if present — the photo-first drill-down lives here.
    const windowSection = useMemo(
        () => sections.find(s => s.baseType === BaseType.Window),
        [sections]
    );

    // The currently enlarged reference photo group.
    const activeReferencePhotoGroup = useMemo(
        () => windowSection?.referencePhotoGroups?.find(g => g.key === activeReferencePhotoKey) ?? null,
        [windowSection, activeReferencePhotoKey]
    );

    // Photos of the currently selected window, used to scope keyboard navigation so
    // arrow keys never jump to a thumbnail that isn't visible in the drill-down.
    const activeWindowImages = useMemo((): SelectedImage[] | null => {
        if (!activeWindowKey || !activeReferencePhotoGroup) return null;
        const marker = activeReferencePhotoGroup.markers.find(m => m.componentKey === activeWindowKey);
        if (!marker) return null;
        return marker.subSection.imagesByYear.flatMap(y => y.images);
    }, [activeWindowKey, activeReferencePhotoGroup]);

    // Auto-select the first reference photo when the section appears or the building
    // changes; keep the current selection if it is still valid.
    useEffect(() => {
        const groups = windowSection?.referencePhotoGroups;
        if (!groups || groups.length === 0) {
            setActiveReferencePhotoKey(null);
            return;
        }
        setActiveReferencePhotoKey(prev => (prev && groups.some(g => g.key === prev)) ? prev : groups[0].key);
    }, [windowSection]);

    // Selecting a different reference photo clears the active marker/window (AE4).
    const handleSelectReferencePhoto = useCallback((key: string) => {
        setActiveReferencePhotoKey(key);
        setActiveWindowKey(null);
        setSelectedLocation(null);
    }, []);

    const handleSelectMarker = useCallback((componentKey: string) => {
        setActiveWindowKey(componentKey);
        setSelectedLocation(null);
    }, []);

    // Upload modal state
    const [uploadModalOpen, setUploadModalOpen] = useState(false);

    // Delete confirmation modal state
    const [deleteTarget, setDeleteTarget] = useState<SelectedImage | null>(null);
    const deleteModalOpen = deleteTarget !== null;
    const [uploadFiles, setUploadFiles] = useState<File[]>([]);
    const [uploadTarget, setUploadTarget] = useState<{
        component?: BaseComponent;
        room?: RoomModel;
    }>({});

    // Drag and drop state
    const [dragOverSection, setDragOverSection] = useState<string | null>(null);

    // File input ref for upload
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Sidebar ref for scroll position preservation
    const sidebarRef = useRef<HTMLDivElement>(null);

    // Tier 3 (selected window) ref — used to scroll it into view on marker select.
    const tier3Ref = useRef<HTMLDivElement>(null);

    // Phones show either the list or the open image, never both. Going back
    // returns the list to where it was scrolled; there the whole page scrolls,
    // not the sidebar.
    const isSmallScreen = useIsSmallScreen();
    const mobileView = isSmallScreen && (selectedImage || selectedLocation) ? 'preview' : 'list';
    const containerRef = useRef<HTMLDivElement>(null);
    const listScrollTopRef = useRef(0);

    useLayoutEffect(() => {
        const scroller = containerRef.current?.closest('.main-content');
        if (!scroller) return;
        scroller.scrollTop = mobileView === 'list' ? listScrollTopRef.current : 0;
    }, [mobileView]);

    const rememberListScroll = useCallback(() => {
        listScrollTopRef.current = containerRef.current?.closest('.main-content')?.scrollTop ?? 0;
    }, []);

    const handleBackToList = useCallback(() => {
        setSelectedLocation(null);
        setSelectedImage(null);
    }, [setSelectedImage]);

    // Handle add button click on a subsection
    const handleAddClick = useCallback((subSection: SubSection) => {
        // Get the actual component or room from the building structure
        if (subSection.componentKey && currentBuilding?.model?.structure?.components) {
            const component = currentBuilding.model.structure.components[subSection.componentKey];
            if (component) {
                setUploadTarget({ component });
            }
        } else if (subSection.roomKey && currentBuilding?.model?.structure?.rooms) {
            const room = currentBuilding.model.structure.rooms[subSection.roomKey];
            if (room) {
                setUploadTarget({ room });
            }
        }
        fileInputRef.current?.click();
    }, [currentBuilding]);

    // Drag and drop handlers
    const handleDragOver = useCallback((e: React.DragEvent, sectionKey: string) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOverSection(sectionKey);
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOverSection(null);
    }, []);

    const handleDrop = useCallback((e: React.DragEvent, subSection: SubSection) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOverSection(null);

        const files = Array.from(e.dataTransfer.files).filter(file =>
            file.type.startsWith('image/') || file.name.toLowerCase().endsWith('.heic')
        );

        if (files.length > 0) {
            // Get the actual component or room from the building structure
            if (subSection.componentKey && currentBuilding?.model?.structure?.components) {
                const component = currentBuilding.model.structure.components[subSection.componentKey];
                if (component) {
                    setUploadTarget({ component });
                }
            } else if (subSection.roomKey && currentBuilding?.model?.structure?.rooms) {
                const room = currentBuilding.model.structure.rooms[subSection.roomKey];
                if (room) {
                    setUploadTarget({ room });
                }
            }
            setUploadFiles(files);
            setUploadModalOpen(true);
        }
    }, [currentBuilding]);

    // Handle file selection
    const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        if (files.length > 0) {
            setUploadFiles(files);
            setUploadModalOpen(true);
        }
        // Reset input
        if (e.target) {
            e.target.value = '';
        }
    }, []);

    // Handle upload complete
    const handleUploadComplete = useCallback(async () => {
        // Save scroll position
        const scrollTop = sidebarRef.current?.scrollTop || 0;

        setUploadModalOpen(false);
        setUploadFiles([]);
        setUploadTarget({});
        // Refetch building to show new images
        await refetchBuilding();

        // Restore scroll position after React re-renders
        requestAnimationFrame(() => {
            if (sidebarRef.current) {
                sidebarRef.current.scrollTop = scrollTop;
            }
        });
    }, [refetchBuilding]);

    // Open delete confirmation modal
    const handleDeleteClick = useCallback(() => {
        if (!selectedImage) return;
        setDeleteTarget(selectedImage);
    }, [selectedImage]);

    // Confirm delete
    const handleDeleteConfirm = useCallback(async () => {
        if (!deleteTarget) return;

        // Save scroll position
        const scrollTop = sidebarRef.current?.scrollTop || 0;

        setDeleteTarget(null);
        await deleteImage(deleteTarget);

        // Restore scroll position after React re-renders
        requestAnimationFrame(() => {
            if (sidebarRef.current) {
                sidebarRef.current.scrollTop = scrollTop;
            }
        });
    }, [deleteTarget, deleteImage]);

    // Phones open a thumbnail straight into the full-screen viewer, with the rest
    // of that component's or room's photos a swipe away. Its toolbar acts on the
    // photo being shown.
    const openPath = useOpenPath();
    const openingGalleryRef = useRef(false);

    const openPhoneGallery = useCallback(async (images: SelectedImage[], startIndex: number) => {
        if (openingGalleryRef.current || !buildingKey) return;
        openingGalleryRef.current = true;
        let urls: string[];
        try {
            urls = await Promise.all(images.map(image => resolveFirebaseImageUrl(image.photo.storagePath)));
        } catch (err) {
            console.error('Error loading images for viewer:', err);
            return;
        } finally {
            openingGalleryRef.current = false;
        }

        const currentImage = (toolbar: { instance: Fancybox }) =>
            images[toolbar.instance.getSlide()?.index ?? startIndex];
        const goToLabel = images[startIndex].type === 'component' ? 'Gå till komponent' : 'Gå till rum';

        Fancybox.show(
            images.map((image, i) => ({
                src: urls[i],
                caption: viewerCaption(image),
            })),
            {
                startIndex,
                Toolbar: {
                    display: {
                        left: ['infobar'],
                        middle: [],
                        right: isAdmin ? ['goToDetail', 'deletePhoto', 'close'] : ['goToDetail', 'close'],
                    },
                    items: {
                        goToDetail: {
                            tpl: `<button class="f-button" title="${goToLabel}" aria-label="${goToLabel}">${OPEN_ICON}</button>`,
                            click: (toolbar, event) => {
                                const image = currentImage(toolbar);
                                toolbar.instance.close();
                                openPath(detailPathOf(buildingKey, image), event instanceof MouseEvent ? event : undefined);
                            },
                        },
                        deletePhoto: {
                            tpl: `<button class="f-button" title="Ta bort bild" aria-label="Ta bort bild">${TRASH_ICON}</button>`,
                            click: (toolbar) => {
                                const image = currentImage(toolbar);
                                toolbar.instance.close();
                                setDeleteTarget(image);
                            },
                        },
                    },
                },
            }
        );
    }, [buildingKey, isAdmin, openPath]);

    const handleThumbnailClick = useCallback((image: SelectedImage, subSection: SubSection) => {
        if (!isSmallScreen) {
            handleSelectImage(image);
            return;
        }
        const images = subSection.imagesByYear.flatMap(yearGroup => yearGroup.images);
        const index = images.findIndex(img => img.photo.key === image.photo.key);
        openPhoneGallery(images, Math.max(index, 0));
    }, [isSmallScreen, handleSelectImage, openPhoneGallery]);

    // Keyboard navigation. When drilled into a window, scope to that window's photos
    // so arrow keys stay within the visible Tier 3 set; otherwise navigate the flat
    // visible set (allImages).
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            const navList = activeWindowImages ?? allImages;
            if (uploadModalOpen || deleteModalOpen || navList.length === 0) return;

            // Don't navigate if focus is on an input
            if (document.activeElement?.tagName === 'INPUT' ||
                document.activeElement?.tagName === 'TEXTAREA') return;

            const currentIndex = selectedImage
                ? navList.findIndex(img => img.photo.key === selectedImage.photo.key)
                : -1;

            if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
                e.preventDefault();
                const prevIndex = currentIndex > 0 ? currentIndex - 1 : navList.length - 1;
                handleSelectImage(navList[prevIndex]);
            } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
                e.preventDefault();
                const nextIndex = currentIndex >= 0 && currentIndex < navList.length - 1 ? currentIndex + 1 : 0;
                handleSelectImage(navList[nextIndex]);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [allImages, activeWindowImages, selectedImage, handleSelectImage, uploadModalOpen, deleteModalOpen]);

    // On marker select, bring the selected window (Tier 3) into view if it isn't
    // already adequately visible — otherwise selecting a marker on a photo near the
    // bottom edge feels like nothing happened. No-op when already in view.
    useEffect(() => {
        if (!activeWindowKey) return;
        const el = tier3Ref.current;
        const container = isSmallScreen ? el?.closest('.main-content') : sidebarRef.current;
        if (!el || !container) return;

        const cRect = container.getBoundingClientRect();
        const tRect = el.getBoundingClientRect();
        // Enough of the block (component header + first photo row) is on screen.
        const desiredVisible = Math.min(220, tRect.height);
        const alreadyVisible = tRect.top >= cRect.top && (cRect.bottom - tRect.top) >= desiredVisible;
        if (alreadyVisible) return;

        // Scroll so the block's top sits just below the container's top edge.
        container.scrollBy({ top: tRect.top - cRect.top - 12, behavior: 'smooth' });
    }, [activeWindowKey, isSmallScreen]);

    // Check for empty state
    const hasAnyImages = sections.some(s => s.totalCount > 0);

    if (loading) {
        return <div className="unified-images-loading">Laddar...</div>;
    }

    if (!buildingKey) {
        return <div className="unified-images-error">Byggnadsnyckel saknas</div>;
    }

    if (!currentBuilding) {
        return <div className="unified-images-error">Byggnad saknas</div>;
    }

    // Renders one component's or room's block (header + location thumbnails + year
    // groups), shared by every section layout. Under a room header the placement
    // line is left out, since the level and room headers already say it.
    const renderSubSectionBlock = (subSection: SubSection, showPlacement: boolean) => {
        const component = subSection.componentKey
            ? currentBuilding.model?.structure?.components?.[subSection.componentKey]
            : undefined;
        // Phones add photos from a tile at the start of the grid instead of a header button
        const addTile = isSmallScreen && (
            <button
                type="button"
                className="unified-images-add-tile"
                onClick={() => handleAddClick(subSection)}
                aria-label={`Lägg till bilder: ${subSection.label}`}
            >
                <ImagePlus size={22} aria-hidden="true" />
                Lägg till
            </button>
        );
        return (
            <div key={subSection.key} className="unified-images-subsection">
                <SubSectionHeader
                    subSection={subSection}
                    buildingKey={buildingKey}
                    isDragOver={dragOverSection === subSection.key}
                    showAddButton={!isSmallScreen}
                    showPlacement={showPlacement}
                    onAddClick={() => handleAddClick(subSection)}
                    onDragOver={(e) => handleDragOver(e, subSection.key)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, subSection)}
                />
                {component && (
                    <ComponentLocationThumbnails
                        component={component}
                        building={currentBuilding}
                        onLocationClick={handleSelectLocation}
                    />
                )}
                <div
                    className={`unified-images-subsection-content ${dragOverSection === subSection.key ? 'drag-over' : ''}`}
                    onDragOver={(e) => handleDragOver(e, subSection.key)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, subSection)}
                >
                    {subSection.imagesByYear.length === 0 ? (
                        addTile
                            ? <div className="unified-images-thumbnails">{addTile}</div>
                            : <div className="unified-images-empty-window">Inga bilder än</div>
                    ) : subSection.imagesByYear.map((yearGroup, yearIndex) => (
                        <div key={yearGroup.year} className="unified-images-year-group">
                            <div className="unified-images-year-label">{yearGroup.year}</div>
                            <div className="unified-images-thumbnails">
                                {yearIndex === 0 && addTile}
                                {yearGroup.images.map(image => (
                                    <ImageThumbnail
                                        key={image.photo.key}
                                        image={image}
                                        isSelected={selectedImage?.photo.key === image.photo.key}
                                        onClick={() => handleThumbnailClick(image, subSection)}
                                    />
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };
    const renderSubSection = (subSection: SubSection) => renderSubSectionBlock(subSection, true);
    const renderRoomSubSection = (subSection: SubSection) => renderSubSectionBlock(subSection, false);

    return (
        <div className="unified-images-container" ref={containerRef} data-mobile-view={mobileView}>
            {/* Header */}
            <div className="unified-images-header">
                <BreadcrumbComponent
                    items={[
                        { text: currentBuilding.summary.name ?? 'Byggnad', link: `/building/${buildingKey}` },
                        { text: 'Samtliga bilder' }
                    ]}
                />
            </div>

            {/* Error display */}
            {error && (
                <div className="unified-images-error-banner">
                    {error}
                    <button onClick={clearError}>×</button>
                </div>
            )}

            {/* Empty state */}
            {!hasAnyImages ? (
                <div className="unified-images-empty">
                    <p>Inga bilder att visa</p>
                    <p style={{ fontSize: '14px', color: '#888', marginTop: '8px' }}>
                        Lägg till bilder från en komponent eller rum i byggnaden
                    </p>
                </div>
            ) : (
                /* Main content */
                <div className="unified-images-content">
                    {/* Sidebar */}
                    <div className="unified-images-sidebar" ref={sidebarRef} onClickCapture={rememberListScroll}>
                        <div className="unified-images-sidebar-toolbar">
                            <button
                                className={`unified-images-collapse-all-button${sections.every(s => s.collapsed) ? ' all-collapsed' : ''}`}
                                onClick={collapseAll}
                            >
                                Stäng alla
                            </button>
                        </div>
                        {sections.map(section => (
                            <div key={section.key} className="unified-images-section">
                                <SectionHeader
                                    section={section}
                                    onToggle={() => toggleSection(section.key)}
                                    utilityGroupingMode={utilityGroupingMode}
                                    onGroupingModeChange={setUtilityGroupingMode}
                                />
                                {!section.collapsed && (
                                    <div className="unified-images-section-content">
                                        {/* Render levelGroups for Fastighetsteknik in room mode */}
                                        {isRoomGroupedImageSection(section.baseType) && utilityGroupingMode === 'room' && section.levelGroups?.map(levelGroup => (
                                            <div key={levelGroup.levelKey} className="unified-images-level-group">
                                                <LevelHeader levelGroup={levelGroup} />
                                                {levelGroup.rooms.map(roomGroup => (
                                                    <div key={roomGroup.roomKey} className="unified-images-room-group">
                                                        <RoomHeader roomGroup={roomGroup} buildingKey={buildingKey} />
                                                        {roomGroup.subSections.map(renderRoomSubSection)}
                                                    </div>
                                                ))}
                                            </div>
                                        ))}

                                        {/* Render categoryGroups for Fastighetsteknik in category mode */}
                                        {isRoomGroupedImageSection(section.baseType) && utilityGroupingMode === 'category' && section.categoryGroups?.map(categoryGroup => (
                                            <div key={categoryGroup.categoryKey} className="unified-images-category-group">
                                                <CategoryHeader categoryGroup={categoryGroup} />
                                                {categoryGroup.subSections.map(renderSubSection)}
                                            </div>
                                        ))}

                                        {/* Fönster: photo-first drill-down (reference photos → markers → window) */}
                                        {section.baseType === BaseType.Window && (
                                            <div className="unified-images-refphoto">
                                                {/* Tier 1: reference-photo thumbnails, each with its window markers */}
                                                {section.referencePhotoGroups && section.referencePhotoGroups.length > 0 && (
                                                    <div className="unified-images-refphoto-row">
                                                        {section.referencePhotoGroups.map((group) => (
                                                            <div
                                                                key={group.key}
                                                                className={`unified-images-refphoto-thumb ${group.key === activeReferencePhotoKey ? 'selected' : ''}`}
                                                            >
                                                                <MultiMarkerImage
                                                                    storagePath={group.storagePath}
                                                                    imageSize={ImageSize.THUMBNAIL_SMALL}
                                                                    fit="cover"
                                                                    activeKey={activeWindowKey}
                                                                    markers={group.markers.map(m => ({ key: m.componentKey, x: m.x, y: m.y, width: m.width, height: m.height, label: m.displayName }))}
                                                                    onImageClick={() => handleSelectReferencePhoto(group.key)}
                                                                    alt={group.label}
                                                                />
                                                                <div className="unified-images-refphoto-label" title={group.label}>
                                                                    {group.label}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}

                                                {/* Tier 2: enlarged photo with clickable markers */}
                                                {activeReferencePhotoGroup && (
                                                    <div className="unified-images-refphoto-stage">
                                                        <MultiMarkerImage
                                                            storagePath={activeReferencePhotoGroup.storagePath}
                                                            imageSize={ImageSize.FULL}
                                                            fit="contain"
                                                            interactive
                                                            activeKey={activeWindowKey}
                                                            markers={activeReferencePhotoGroup.markers.map(m => ({ key: m.componentKey, x: m.x, y: m.y, width: m.width, height: m.height, label: m.displayName }))}
                                                            onMarkerClick={handleSelectMarker}
                                                            alt="Vald fasadbild"
                                                        />
                                                        <div className="unified-images-refphoto-hint">
                                                            Klicka på en markör för att visa fönstrets bilder
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Tier 3: selected window's photos, year by year */}
                                                {activeReferencePhotoGroup && activeWindowKey && (() => {
                                                    const marker = activeReferencePhotoGroup.markers.find(m => m.componentKey === activeWindowKey);
                                                    return marker ? <div ref={tier3Ref}>{renderSubSection(marker.subSection)}</div> : null;
                                                })()}

                                                {/* Omarkerade fönster: windows without any photoLocations pin */}
                                                {section.unmarkedSubSections && section.unmarkedSubSections.length > 0 && (
                                                    <div className="unified-images-omarkerade-group">
                                                        <div className="unified-images-omarkerade-header">Omarkerade fönster</div>
                                                        {section.unmarkedSubSections.map(renderSubSection)}
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Render flat subSections for other base types (not Utility or Fönster) */}
                                        {!isRoomGroupedImageSection(section.baseType) && section.baseType !== BaseType.Window && section.subSections.map(renderSubSection)}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Preview pane */}
                    <div className="unified-images-preview">
                        {mobileView === 'preview' && (
                            <div className="unified-images-preview-toolbar">
                                <button type="button" className="unified-images-back-button" onClick={handleBackToList}>
                                    <ChevronLeft size={20} aria-hidden="true" />
                                    Alla bilder
                                </button>
                            </div>
                        )}
                        {selectedLocation ? (
                            <div className="unified-images-preview-container">
                                <div className="unified-images-preview-image-wrapper">
                                    {/* Phones: the page scrolls, so the pane has no height to fit */}
                                    <LocationViewer
                                        location={selectedLocation.location}
                                        component={selectedLocation.component}
                                        building={currentBuilding}
                                        fitHeight={!isSmallScreen}
                                    />
                                </div>
                                <div className="unified-images-preview-info">
                                    <div className="unified-images-preview-name">
                                        {selectedLocation.location.label}
                                    </div>
                                    <div className="unified-images-location-actions">
                                        <button
                                            type="button"
                                            className="unified-images-location-button"
                                            onClick={() => setLocationFullscreen(true)}
                                        >
                                            <Maximize2 size={16} aria-hidden="true" />
                                            Helskärm
                                        </button>
                                        <button
                                            type="button"
                                            className="unified-images-location-button"
                                            onClick={() => setSelectedLocation(null)}
                                        >
                                            Tillbaka till bild
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ) : selectedImage ? (
                            <ImagePreview
                                image={selectedImage}
                                isAdmin={isAdmin}
                                deleting={deleting}
                                onDelete={handleDeleteClick}
                                detailPath={detailPathOf(buildingKey ?? '', selectedImage)}
                            />
                        ) : (
                            <div className="unified-images-preview-placeholder">
                                Välj en bild från listan för att visa förhandsvisning
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* The selected position full screen */}
            {locationFullscreen && selectedLocation && (
                <LocationOverlay
                    location={selectedLocation.location}
                    component={selectedLocation.component}
                    building={currentBuilding}
                    onClose={() => setLocationFullscreen(false)}
                />
            )}

            {/* Hidden file input for uploads */}
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*,.heic,.HEIC"
                multiple
                style={{ display: 'none' }}
                onChange={handleFileSelect}
            />

            {/* Upload modal */}
            {uploadModalOpen && currentBuilding && (
                <ImageUploadModal
                    isOpen={uploadModalOpen}
                    onClose={() => setUploadModalOpen(false)}
                    onSubmit={handleUploadComplete}
                    files={uploadFiles}
                    component={uploadTarget.component}
                    room={uploadTarget.room}
                    building={currentBuilding}
                    buildingKey={buildingKey}
                />
            )}

            {/* Delete confirmation modal */}
            {deleteModalOpen && (
                <div className="unified-images-modal-overlay" onClick={() => setDeleteTarget(null)}>
                    <div className="unified-images-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="unified-images-modal-header">
                            <h3>Ta bort bild</h3>
                        </div>
                        <div className="unified-images-modal-body">
                            <p>Är du säker på att du vill ta bort denna bild?</p>
                        </div>
                        <div className="unified-images-modal-footer">
                            <button
                                className="unified-images-modal-cancel"
                                onClick={() => setDeleteTarget(null)}
                            >
                                Avbryt
                            </button>
                            <button
                                className="unified-images-modal-confirm"
                                onClick={handleDeleteConfirm}
                                disabled={deleting}
                            >
                                {deleting ? 'Tar bort...' : 'Ta bort'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UnifiedBuildingImages;
